"use client"

import { useState, useEffect, useSyncExternalStore } from "react"

/** Breakpoints: default (<640px), sm (640px+), md (768px+), lg (1024px+), xl (1280px+), 2xl (1536px+) */
export type ShowLengthBreakpoints = {
  default?: number
  sm?: number
  md?: number
  lg?: number
  xl?: number
  "2xl"?: number
}

interface ReadMoreProps {
  text: string
  /** Character count to show when collapsed - single value or per breakpoint */
  showlength: number | ShowLengthBreakpoints
  toggleClassName?: string
  className?: string
}

const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const

/**
 * Start from `default`, then override with each matched breakpoint that is set.
 * So `{ default: 10 }` stays 10 on laptop; `{ default: 10, lg: 200 }` grows at lg+.
 */
function getShowLengthForWidth(
  showlength: number | ShowLengthBreakpoints,
  width: number,
  fallback: number
): number {
  if (typeof showlength === "number") return showlength

  let result = showlength.default ?? fallback
  if (width >= BREAKPOINTS.sm && showlength.sm !== undefined) result = showlength.sm
  if (width >= BREAKPOINTS.md && showlength.md !== undefined) result = showlength.md
  if (width >= BREAKPOINTS.lg && showlength.lg !== undefined) result = showlength.lg
  if (width >= BREAKPOINTS.xl && showlength.xl !== undefined) result = showlength.xl
  if (width >= BREAKPOINTS["2xl"] && showlength["2xl"] !== undefined) {
    result = showlength["2xl"]
  }
  return result
}

const subscribeToViewport = (onStoreChange: () => void) => {
  window.addEventListener("resize", onStoreChange)
  return () => window.removeEventListener("resize", onStoreChange)
}

const getViewportWidth = () => window.innerWidth
const getServerViewportWidth = () => 0

const ReadMore = ({
  text,
  showlength,
  toggleClassName = "cursor-pointer text-blackishBrown font-semibold text-nowrap",
  className,
}: ReadMoreProps) => {
  const [readMore, setReadMore] = useState(false)
  const fallback =
    typeof showlength === "number" ? showlength : (showlength.default ?? 100)

  const viewportWidth = useSyncExternalStore(
    subscribeToViewport,
    getViewportWidth,
    getServerViewportWidth
  )

  useEffect(() => {
    setReadMore(false)
  }, [text])

  const effectiveShowLength =
    typeof showlength === "number"
      ? showlength
      : getShowLengthForWidth(showlength, viewportWidth, fallback)

  const trimmed = text?.trim() ?? ""
  const realLength = trimmed.length

  if (!trimmed) return null

  const isTruncated = realLength > effectiveShowLength
  const displayText = readMore ? trimmed : trimmed.slice(0, effectiveShowLength)

  return (
    <p className={className}>
      {displayText}
      {isTruncated && !readMore ? <span>...</span> : null}
      {isTruncated
        ? readMore ? (
            <span
              className={toggleClassName}
              role="button"
              tabIndex={0}
              onClick={() => setReadMore(false)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  setReadMore(false)
                }
              }}
            >
              {" "}
              Read Less
            </span>
          ) : (
            <span
              className={toggleClassName}
              role="button"
              tabIndex={0}
              onClick={() => setReadMore(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  setReadMore(true)
                }
              }}
            >
              Read More
            </span>
          )
        : null}
    </p>
  )
}

export default ReadMore
